import React from 'react';
import dynamic from 'next/dynamic';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { TimeRange } from '../models/TimeRange.js';
import { Link } from './Link.js';
import { MissionResultMap } from '../models/mission.js';
import { MissionWindow } from './MissionWindow.js';
import { DetailTable } from './DetailTable.js';

const CountDownClock = dynamic(() => import('./CountDownClock.js').then(m => m.CountDownClock), {
  ssr: false
});
const MissionDetailTable = ({
  item,
  sx
}) => {
  var _MissionResultMap$ite, _item$launch_designat;
  const theme = useTheme();
  const statusMap = (_MissionResultMap$ite = MissionResultMap[item.status]) !== null && _MissionResultMap$ite !== void 0 ? _MissionResultMap$ite : MissionResultMap.UNKNOWN;

  // テーマカラーを正しく取得
  const getThemeColor = colorPath => {
    const paths = colorPath.split('.');
    let color = theme.palette;
    for (const path of paths) {
      color = color[path];
    }
    return color;
  };
  const tableItems = [{
    key: '状態',
    value: /*#__PURE__*/React.createElement(Typography, {
      component: "span",
      sx: {
        color: getThemeColor(statusMap.color)
      }
    }, `${statusMap.content} (${item.status})`)
  }, {
    key: '日時',
    value: item.datetime_format
  }];

  // カウントダウン行を条件付きで追加
  if (item.datetime_time_type === 'CONFIRMED') {
    tableItems.push({
      key: 'カウントダウン',
      value: /*#__PURE__*/React.createElement(CountDownClock, {
        datetime_iso: item.datetime
      })
    });
  }

  // 打ち上げウィンドウ行を条件付きで追加
  // 表示条件:
  // 1. window_startが存在する
  // 2. window_endが存在する
  // 3. window_endとwindow_startが異なる（瞬間打ち上げではない）
  if (item.window_start && item.window_end && item.window_end !== item.window_start) {
    const liftoffTime = item.datetime_time_type === 'CONFIRMED' ? item.datetime : new Date().toISOString().slice(0, 19) + 'Z';
    tableItems.push({
      key: '打ち上げウィンドウ',
      value: /*#__PURE__*/React.createElement(MissionWindow, {
        liftoffTime: liftoffTime,
        windowOpen: item.window_start,
        windowClose: item.window_end
      })
    });
  }

  // 識別符号行を条件付きで追加
  if (Boolean((_item$launch_designat = item.launch_designator) == null ? void 0 : _item$launch_designat.length)) {
    var _item$tle_list;
    tableItems.push({
      key: '識別符号',
      value: /*#__PURE__*/React.createElement(React.Fragment, null, (_item$tle_list = item.tle_list) != null && _item$tle_list.length ? /*#__PURE__*/React.createElement(Link, {
        href: `/q/${item.type}/i/${item.pk}/satellites/`
      }, item.launch_designator, ' ', /*#__PURE__*/React.createElement(SearchIcon, {
        sx: {
          fontSize: '0.8rem',
          ml: 0.25,
          mt: 0.5,
          transform: 'scale(-1, 1)'
        }
      })) : item.launch_designator)
    });
  }

  // launch特有の項目を追加
  if (item.type === 'launch') {
    if (item.weather_probability) {
      var _item$weather_concern;
      tableItems.push({
        key: '天候',
        value: /*#__PURE__*/React.createElement(React.Fragment, null, item.weather_probability, "% GO", /*#__PURE__*/React.createElement("br", null), ((_item$weather_concern = item.weather_concerns) == null ? void 0 : _item$weather_concern.length) && /*#__PURE__*/React.createElement("span", null, "\u61F8\u5FF5\u4E8B\u9805: ", item.weather_concerns))
      });
    }
    tableItems.push({
      key: 'ミッション概要',
      value: item.overview
    }, {
      key: 'ロケット',
      value: /*#__PURE__*/React.createElement(Link, {
        href: `/mission?rocket=${encodeURIComponent(item.rocket || '')}`
      }, item.rocket)
    }, {
      key: '打ち上げ事業者',
      value: /*#__PURE__*/React.createElement(Link, {
        href: `/mission?provider=${encodeURIComponent(item.provider || '')}`
      }, item.provider)
    }, {
      key: '発射場',
      value: /*#__PURE__*/React.createElement(Link, {
        href: `/mission?site=${encodeURIComponent(item.site || '')}`
      }, item.site)
    });
    if (item.recovery_events && item.recovery_events.length > 0) {
      tableItems.push({
        key: '回収イベント',
        value: item.recovery_events.map(ev => {
          var _ev$reuse_num, _ev$location;
          const status = MissionResultMap[ev.result] || MissionResultMap.UNKNOWN;
          return /*#__PURE__*/React.createElement(Box, {
            key: `${ev.vehicle_name}-${ev.datetime}`,
            sx: {
              color: getThemeColor(status.color)
            }
          }, `${ev.vehicle_name} #${(_ev$reuse_num = ev.reuse_num) !== null && _ev$reuse_num !== void 0 ? _ev$reuse_num : '-'}`, /*#__PURE__*/React.createElement("br", null), `${(_ev$location = ev.location) !== null && _ev$location !== void 0 ? _ev$location : ''} (${status.content})`);
        })
      });
    }
  }

  // 関連リンク
  tableItems.push({
    key: '関連リンク',
    value: (item.links || []).map(link => /*#__PURE__*/React.createElement(Box, {
      key: link.url
    }, /*#__PURE__*/React.createElement(Link, {
      href: link.url,
      external: true
    }, link.title)))
  });

  // 集会
  tableItems.push({
    key: '集会',
    value: (item.meetup || []).map(meetup => /*#__PURE__*/React.createElement(Box, {
      key: meetup.pk
    }, /*#__PURE__*/React.createElement(Link, {
      href: `/meetup/detail?pk=${meetup.pk}`
    }, meetup.type, ": ", meetup.datetime_format)))
  });

  // もっと調べる
  tableItems.push({
    key: 'もっと調べる',
    value: /*#__PURE__*/React.createElement(Link, {
      href: `/mission?datetime=${encodeURIComponent(TimeRange.fromMode(item.datetime, 'YEAR').toString().replace('datetime=', ''))}`
    }, item.datetime.split('-')[0], "\u5E74 \u306E\u30DF\u30C3\u30B7\u30E7\u30F3\u4E00\u89A7")
  });
  return /*#__PURE__*/React.createElement(DetailTable, {
    items: tableItems,
    sx: {
      ...sx,
      '& .MuiTableCell:first-of-type': {
        color: getThemeColor(statusMap.color)
      }
    }
  });
};

export { MissionDetailTable, MissionDetailTable as default };
